import React, { isValidElement, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useAtom, useAtomValue } from 'jotai';
import {
  notifyStartCallAtom,
  notifyEndCallAtom,
  notifyUpdateCallAtom,
} from '../../../Atoms/HubAtoms';
import { allOutcomesAtom } from '../../../Atoms';
import { Tooltip, Stack } from '@mui/material';
import { addZero, culture, compareDates } from '../../../Helpers/dates';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import StatusChip from '../../Chip/StatusChip';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';

const CallStatusesOptions = {
  starting: (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        justifyContent: "center",
        alignItems: "center"
      }}>
      <div className="dot-flashing" />
    </Stack>
  ),
  connected: PhoneInTalkRoundedIcon,
  disconnected: CallEndRoundedIcon,
  neverCalled: null,
  lastCallDate: CallRoundedIcon,
  neverCallAgain: DoNotDisturbAltIcon,
  answeredCall: DoneIcon,
  notAnsweredCall: CloseIcon,
};

const statusesColors = {
  connected: '#1D5B9D',
  disconnected: '#fca600',
  neverCalled: '#BCBCBC',
  lastCallDate: '#c6cbf0',
  neverCallAgain: '#5AC4FF',
  answeredCall: '#34A853',
  notAnsweredCall: '#b00020',
  lastActivity: '#0288d1',
};

const CallStatusChip = function CallStatusChip({
  data,
  updateItem,
  calendarOptionEnabled,
}) {
  const intl = useIntl();

  const allOutcomes = useAtomValue(allOutcomesAtom);
  const [notifyStartCall, setNotifyStartCall] = useAtom(notifyStartCallAtom);
  const [notifyUpdateCall, setNotifyUpdateCall] = useAtom(notifyUpdateCallAtom);
  const [notifyEndCall, setNotifyEndCall] = useAtom(notifyEndCallAtom);

  /** START CALL EVENT **/
  useEffect(() => {
    if (
      notifyStartCall &&
      notifyStartCall?.baseEntityId === data?.id &&
      updateItem
    ) {
      const { outcome, callStatus, createdByFullName } = notifyStartCall || {};
      updateItem({
        ...data,
        lastCallOutcome: outcome,
        latCallCreatedFullName: createdByFullName,
        lastCallStatus: callStatus,
        lastActivityType: 'CALL',
      });
      setNotifyStartCall(null);
    }
  }, [notifyStartCall]);

  /** UPDATE CALL EVENT **/
  useEffect(() => {
    if (
      notifyUpdateCall &&
      notifyUpdateCall?.baseEntityId === data?.id &&
      updateItem
    ) {
      const { callStatus } = notifyUpdateCall || {};
      updateItem({
        ...data,
        lastCallStatus: callStatus,
      });
      setNotifyUpdateCall(null);
    }
  }, [notifyUpdateCall]);

  /** END CALL EVENT **/
  useEffect(() => {
    if (
      notifyEndCall &&
      notifyEndCall?.baseEntityId === data?.id &&
      updateItem
    ) {
      const { callStatus, completedOn, outcome, completedByFullName } =
        notifyEndCall || {};

      updateItem({
        ...data,
        lastCallStatus: callStatus,
        lastCallOutcome: outcome,
        lastCallCompletedOn: completedOn,
        latCallCreatedFullName: completedByFullName,
        removedOn: allOutcomes.find((o) => o.id === outcome)?.removeCustomer
          ? new Date()
          : undefined,
      });
      setNotifyEndCall(null);
    }
  }, [notifyEndCall]);

  const callStatus = useMemo(() => {
    if (
      calendarOptionEnabled &&
      data?.lastActivityType &&
      data?.lastActivityType !== 'CALL' && //APPOINTMENT
      data?.lastActivityCompletedOn
    ) {
      return 'lastActivity';
    }
    if (data?.lastCallStatus && !data?.lastCallOutcome)
      return data?.lastCallStatus?.toLowerCase();

    if (!data?.lastCallCompletedOn) return 'neverCalled';

    if (allOutcomes.find((o) => o.id === data?.lastCallOutcome)?.removeCustomer)
      return 'neverCallAgain'; //TODO: vedere se mantenere non richiamare più anche se non è l'ultima chiamata

    if (compareDates(new Date(data?.lastCallCompletedOn), new Date())) {
      const lastOutcome = allOutcomes.find(
        (o) => data?.lastCallOutcome === o.id
      );

      return lastOutcome?.extensionData?.answered
        ? 'answeredCall'
        : 'notAnsweredCall';
    }
    return 'lastCallDate';
  }, [data]);

  return (
    <Tooltip
      title={
        calendarOptionEnabled &&
        callStatus === 'lastActivity' &&
        data?.lastActivityCompletedBy &&
        data?.lastActivityCompletedOn
          ? `${
              intl.messages.PERFORMED_BY
            } ${data?.lastActivityCompletedBy} - ${addZero(
              new Date(data?.lastActivityCompletedOn)
            )}`
          : data?.lastCallCreatedByFullName && data?.lastCallCompletedOn
            ? `${
                intl.messages.PERFORMED_BY
              } ${data?.lastCallCreatedByFullName} - ${addZero(
                new Date(data?.lastCallCompletedOn)
              )}`
            : capitalize(intl.messages[callStatus?.toUpperCase()])
      }
      placement="left"
      arrow
    >
      <span>
        {isValidElement(CallStatusesOptions[callStatus]) ? (
          CallStatusesOptions[callStatus]
        ) : (
          <StatusChip
            alternativeLabel={
              callStatus === 'lastCallDate' && data?.lastCallCompletedOn
                ? `${new Date(data?.lastCallCompletedOn).toLocaleDateString(
                    culture,
                    {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    }
                  )}`
                : callStatus === 'lastActivity' && data?.lastActivityCompletedOn
                  ? `${new Date(
                      data?.lastActivityCompletedOn
                    )?.toLocaleDateString(culture, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}`
                  : null
            }
            status={callStatus}
            options={
              calendarOptionEnabled
                ? { ...CallStatusesOptions, lastActivity: EventRoundedIcon }
                : CallStatusesOptions
            }
            colors={statusesColors}
          />
        )}
      </span>
    </Tooltip>
  );
};

export default CallStatusChip;
